package dev.lingvo.shared

import kotlinx.serialization.Serializable
import kotlinx.serialization.json.JsonObject

@Serializable
data class QaIssueDto(
    val keyId: String,
    val keyName: String,
    val locale: String,
    val check: QaCheck,
    val severity: QaSeverity,
    val message: String,
    val details: JsonObject = JsonObject(emptyMap()),
)

@Serializable
data class ActivityDto(
    val id: String,
    val projectId: String,
    val actorId: String? = null,
    val type: String,
    val createdAt: String,
    val payload: JsonObject = JsonObject(emptyMap()),
)

@Serializable
data class ImportOptions(
    val keepObsolete: Boolean = false,
    /**
     * The number of keys the caller expects to obsolete, sent when that number is above
     * [OBSOLETION_FORCE_THRESHOLD] of the file.
     *
     * A count rather than a `force: true` flag, and the difference is the whole point: a boolean is
     * satisfied by a client that always sets it — and an autonomous agent will always set it — while
     * a count can only be produced by something that first asked what the import would do. The
     * server refuses with `bulk_change_requires_force` when it is missing and
     * `obsolete_count_mismatch` when it disagrees.
     */
    val confirmObsoleteCount: Int? = null,
    /**
     * The dotted-suffix vs. nested-object plural convention this `json_flat` file uses.
     *
     * Absent (`null`) means [PluralStyle.NESTED] — today's behaviour — so a client that predates
     * this field, or an import for any other format, is unaffected. Only `json_flat` reads it.
     */
    val pluralStyle: PluralStyle? = null,
)

@Serializable
data class ImportRequest(
    val path: String,
    val format: FileFormat,
    val locale: String,
    val content: String,
    val fileId: String? = null,
    val options: ImportOptions = ImportOptions(),
    /**
     * The plan being committed, on `POST /import/commit` only.
     *
     * The file itself is re-sent rather than stored — a 40,000-key upload does not belong in a row
     * that lives for ten minutes — so the server checks its hash against the plan's. That is what
     * stops "commit plan X" applying a different file from the one X described.
     */
    val planId: String? = null,
)

@Serializable
data class ImportResultDto(
    val added: Int = 0,
    val updated: Int = 0,
    val unchanged: Int = 0,
    val obsolete: Int = 0,
    /**
     * Keys this file no longer mentions but which were kept.
     *
     * Zero when obsoletion was requested — they were removed, and [obsolete] counts them. Non-zero
     * says "this push would have removed twelve keys under the old default", which is what stops an
     * inverted default turning a team's cleanup script into a silent no-op.
     */
    val keptObsolete: Int = 0,
    /**
     * Keys the file contained that this project does not have. Only a target-locale import can
     * produce these: a translation has nowhere to go until the source locale defines the key. They
     * are counted apart from [unchanged] because a wrong destination file otherwise reports a wall
     * of "unchanged" and reads like a success.
     */
    val skipped: Int = 0,
)

@Serializable
data class ApiErrorBody(
    val error: ApiError,
)

@Serializable
data class ApiError(
    val code: String,
    val message: String,
    val details: JsonObject = JsonObject(emptyMap()),
)
